"use strict";
/**
 * PubMed / NCBI E-utilities: batched EFetch with Entrez History (ESearch usehistory=y)
 * and conservative request spacing for NCBI rate limits.
 *
 * @see https://www.ncbi.nlm.nih.gov/books/NBK25497/
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EFETCH_BATCH_SIZE = exports.PUBMED_ESEARCH_MAX = void 0;
exports.fetchPubMedArticlesSlice = fetchPubMedArticlesSlice;
exports.fetchPubMedArticlesAsRssItems = fetchPubMedArticlesAsRssItems;
const fast_xml_parser_1 = require("fast-xml-parser");
const feedMemoryCache_1 = require("../rss/feedMemoryCache");
const rssRouteLimits_1 = require("../rss/rssRouteLimits");
const EUTILS_BASE = "https://eutils.ncbi.nlm.nih.gov/entrez/eutils";
/** EFetch by PMID list; smaller chunks keep URLs and payloads manageable. */
const PMID_EFECTH_CHUNK = 200;
/** NCBI recommends batches of several hundred records per EFetch (history server). */
const EFETCH_BATCH_SIZE = 500;
exports.EFETCH_BATCH_SIZE = EFETCH_BATCH_SIZE;
/** ESearch via the API returns at most 10,000 UIDs for PubMed. */
const PUBMED_ESEARCH_MAX = 10000;
exports.PUBMED_ESEARCH_MAX = PUBMED_ESEARCH_MAX;
const MONTHS = {
    Jan: 1,
    Feb: 2,
    Mar: 3,
    Apr: 4,
    May: 5,
    Jun: 6,
    Jul: 7,
    Aug: 8,
    Sep: 9,
    Oct: 10,
    Nov: 11,
    Dec: 12,
};
const xmlParser = new fast_xml_parser_1.XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "@_",
    parseAttributeValue: false,
    parseTagValue: false,
    trimValues: true,
});
function minIntervalMs(hasApiKey) {
    return hasApiKey ? 110 : 350;
}
async function sleep(ms) {
    await new Promise((r) => setTimeout(r, ms));
}
function createThrottle(hasApiKey) {
    return { last: 0, minMs: minIntervalMs(hasApiKey) };
}
async function throttleWait(t) {
    const now = Date.now();
    const wait = Math.max(0, t.last + t.minMs - now);
    if (wait > 0)
        await sleep(wait);
    t.last = Date.now();
}
async function fetchWithRetry(url, init, throttle, retries = 3) {
    let lastErr = null;
    for (let attempt = 0; attempt < retries; attempt++) {
        await throttleWait(throttle);
        try {
            const res = await fetch(url, init);
            if (res.ok)
                return res;
            if (res.status === 429 || res.status >= 500) {
                lastErr = new Error(`HTTP ${res.status}`);
                await sleep(1000 * (attempt + 1));
                continue;
            }
            return res;
        }
        catch (e) {
            lastErr = e instanceof Error ? e : new Error("fetch failed");
            await sleep(500 * (attempt + 1));
        }
    }
    throw lastErr ?? new Error("fetch failed");
}
function textFromMixedContent(v) {
    if (v == null)
        return "";
    if (typeof v === "string" || typeof v === "number")
        return String(v);
    if (typeof v === "object" && "#text" in v) {
        const t = v["#text"];
        return t != null ? String(t) : "";
    }
    return "";
}
function normalizePmid(raw) {
    if (raw == null)
        return "";
    if (typeof raw === "number")
        return String(raw);
    if (typeof raw === "string")
        return raw.trim();
    if (typeof raw === "object" && "#text" in raw) {
        return String(raw["#text"] ?? "");
    }
    return "";
}
function abstractToString(abstract) {
    if (abstract == null)
        return "";
    const ab = abstract;
    if (typeof abstract === "string")
        return abstract;
    if (ab.AbstractText == null)
        return "";
    const at = ab.AbstractText;
    if (typeof at === "string")
        return at;
    if (Array.isArray(at)) {
        return at
            .map((segment) => {
            if (typeof segment === "string")
                return segment;
            return textFromMixedContent(segment);
        })
            .filter(Boolean)
            .join("\n\n");
    }
    return textFromMixedContent(at);
}
function formatPubDate(pubDate) {
    if (!pubDate || typeof pubDate !== "object")
        return undefined;
    const p = pubDate;
    if (p.Year == null)
        return undefined;
    const y = Number(p.Year);
    let m = 1;
    const day = p.Day != null ? Number(p.Day) : 1;
    if (typeof p.Month === "number")
        m = p.Month;
    else if (typeof p.Month === "string") {
        if (/^\d+$/.test(p.Month))
            m = Number(p.Month);
        else {
            const key = p.Month.slice(0, 3);
            m = MONTHS[key] ?? 1;
        }
    }
    if (!datePartsValid(y, m, day))
        return undefined;
    try {
        return new Date(Date.UTC(y, m - 1, day)).toUTCString();
    }
    catch {
        return undefined;
    }
}
/** Timestamp for sorting; invalid / missing → 0 */
function pubDateToUtcMs(pubDateStr) {
    if (!pubDateStr?.trim())
        return 0;
    const t = Date.parse(pubDateStr);
    return Number.isFinite(t) ? t : 0;
}
/**
 * When a citation first appears in PubMed (best for chronological feeds).
 * @see https://dtd.nlm.nih.gov/ncbi/pubmed/doc/out/250101/el-PubMedPubDate.html
 */
const HISTORY_STATUS_PRIORITY = [
    "pubmed",
    "entrez",
    "medline",
    "pmc-release",
    "aheadofprint",
];
function formatHistoryPubDate(pubmedData) {
    const pd = pubmedData;
    const history = pd?.History;
    if (!history?.PubMedPubDate)
        return undefined;
    const raw = history.PubMedPubDate;
    const entries = (Array.isArray(raw) ? raw : [raw]).filter((e) => e != null && typeof e === "object");
    for (const status of HISTORY_STATUS_PRIORITY) {
        const hit = entries.find((e) => e["@_PubStatus"] === status);
        const s = hit ? formatPubDate(hit) : undefined;
        if (s)
            return s;
    }
    for (const e of entries) {
        const s = formatPubDate(e);
        if (s)
            return s;
    }
    return undefined;
}
/** Electronic ArticleDate (and similar); Year/Month/Day live on the node, not under .Date */
function formatArticleDate(art) {
    const ad = art.ArticleDate;
    if (ad == null)
        return undefined;
    if (Array.isArray(ad)) {
        const electronic = ad.find((x) => x != null &&
            typeof x === "object" &&
            x["@_DateType"] === "Electronic");
        const chosen = electronic ?? ad[0];
        if (chosen != null && typeof chosen === "object") {
            const o = chosen;
            return formatPubDate(o.Date ?? o);
        }
        return undefined;
    }
    if (typeof ad === "object") {
        const o = ad;
        return formatPubDate(o.Date ?? o);
    }
    return undefined;
}
/**
 * Display / sort date: PubMed index date first (often Feb while issue says Jan),
 * then electronic article date, then journal issue date.
 */
function resolveCitationPubDate(article, art) {
    const journal = art.Journal;
    const issue = journal?.JournalIssue;
    return (formatHistoryPubDate(article.PubmedData) ??
        formatArticleDate(art) ??
        formatPubDate(issue?.PubDate));
}
function datePartsValid(y, m, d) {
    return (Number.isFinite(y) &&
        y > 1600 &&
        m >= 1 &&
        m <= 12 &&
        d >= 1 &&
        d <= 31);
}
function authorsFromArticle(article) {
    const al = article.AuthorList;
    if (!al?.Author)
        return [];
    const raw = al.Author;
    const list = Array.isArray(raw) ? raw : [raw];
    return list
        .map((a) => {
        if (!a || typeof a !== "object")
            return "";
        const au = a;
        const ln = au.LastName != null ? String(au.LastName) : "";
        const initials = au.Initials != null ? String(au.Initials) : "";
        const fore = au.ForeName != null ? String(au.ForeName) : "";
        if (fore && ln)
            return `${fore} ${ln}`.trim();
        if (ln && initials)
            return `${ln} ${initials}`.trim();
        return ln || fore;
    })
        .filter(Boolean);
}
function pubmedArticleToItem(article) {
    const mc = article.MedlineCitation;
    if (!mc)
        return null;
    const pmid = normalizePmid(mc.PMID);
    if (!pmid)
        return null;
    const art = mc.Article;
    if (!art)
        return null;
    const title = typeof art.ArticleTitle === "string"
        ? art.ArticleTitle
        : textFromMixedContent(art.ArticleTitle);
    const pubDate = resolveCitationPubDate(article, art);
    const creators = authorsFromArticle(art);
    const body = abstractToString(art.Abstract);
    return {
        headline: title,
        title,
        body,
        description: body,
        pubDate,
        "dc:creator": creators.length ? creators : undefined,
        author: creators.length ? creators : undefined,
        href: `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`,
        link: `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`,
    };
}
function extractPubmedArticles(parsed) {
    if (!parsed || typeof parsed !== "object")
        return [];
    const set = parsed.PubmedArticleSet;
    if (!set?.PubmedArticle)
        return [];
    const pa = set.PubmedArticle;
    return Array.isArray(pa) ? pa : [pa];
}
function appendEutilsParams(params, opts) {
    params.set("tool", opts.tool);
    params.set("email", opts.email);
    if (opts.apiKey?.trim())
        params.set("api_key", opts.apiKey.trim());
}
function pmidListCacheKey(esearchTerm) {
    if (esearchTerm.length <= 512)
        return esearchTerm;
    let h = 2166136261;
    for (let i = 0; i < esearchTerm.length; i++) {
        h ^= esearchTerm.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return `pmid:${(h >>> 0).toString(16)}:${esearchTerm.length}`;
}
function pmidFromItemHref(href) {
    if (!href)
        return "";
    const m = href.match(/(\d+)\/?$/);
    return m?.[1] ?? "";
}
let pmidListCache = null;
let pubmedSliceCache = null;
function getPmidListCache() {
    if (!pmidListCache) {
        pmidListCache = (0, feedMemoryCache_1.createTtlCache)({
            ttlMs: (0, rssRouteLimits_1.rssMemoryCacheTtlMs)(),
            maxKeys: (0, rssRouteLimits_1.rssMemoryCacheMaxKeys)(),
            staleMs: (0, rssRouteLimits_1.rssMemoryCacheStaleMs)(),
        });
    }
    return pmidListCache;
}
function getPubmedSliceCache() {
    if (!pubmedSliceCache) {
        pubmedSliceCache = (0, feedMemoryCache_1.createTtlCache)({
            ttlMs: (0, rssRouteLimits_1.rssMemoryCacheTtlMs)(),
            maxKeys: (0, rssRouteLimits_1.rssMemoryCacheMaxKeys)(),
            staleMs: (0, rssRouteLimits_1.rssMemoryCacheStaleMs)(),
        });
    }
    return pubmedSliceCache;
}
async function loadPmidList(esearchTerm, retmax, opts, throttle) {
    const cap = Math.min(Math.max(1, retmax), PUBMED_ESEARCH_MAX);
    const key = `${pmidListCacheKey(esearchTerm)}::${cap}`;
    const cache = getPmidListCache();
    const entry = await cache.getOrCreate(key, async () => {
        const esearchUrl = new URL(`${EUTILS_BASE}/esearch.fcgi`);
        esearchUrl.searchParams.set("db", "pubmed");
        esearchUrl.searchParams.set("term", esearchTerm);
        esearchUrl.searchParams.set("retmax", String(cap));
        esearchUrl.searchParams.set("retmode", "json");
        esearchUrl.searchParams.set("sort", "pub+date");
        appendEutilsParams(esearchUrl.searchParams, opts);
        const esRes = await fetchWithRetry(esearchUrl.toString(), { signal: AbortSignal.timeout(60000) }, throttle);
        if (!esRes.ok) {
            throw new Error(`PubMed ESearch failed: HTTP ${esRes.status}`);
        }
        const esJson = (await esRes.json());
        const er = esJson.esearchresult;
        const idlist = er?.idlist;
        const raw = Array.isArray(idlist) ? idlist : idlist ? [idlist] : [];
        const pmids = raw.map(String).filter(Boolean);
        return { pmids };
    });
    return entry.pmids;
}
async function efetchPubmedByPmids(pmids, opts, throttle) {
    const ordered = [];
    for (let i = 0; i < pmids.length; i += PMID_EFECTH_CHUNK) {
        const chunk = pmids.slice(i, i + PMID_EFECTH_CHUNK);
        const efetchUrl = new URL(`${EUTILS_BASE}/efetch.fcgi`);
        efetchUrl.searchParams.set("db", "pubmed");
        efetchUrl.searchParams.set("id", chunk.join(","));
        efetchUrl.searchParams.set("retmode", "xml");
        appendEutilsParams(efetchUrl.searchParams, opts);
        const efRes = await fetchWithRetry(efetchUrl.toString(), { signal: AbortSignal.timeout(120000) }, throttle);
        if (!efRes.ok) {
            throw new Error(`PubMed EFetch failed: HTTP ${efRes.status}`);
        }
        const xmlText = await efRes.text();
        const parsed = xmlParser.parse(xmlText);
        const articles = extractPubmedArticles(parsed);
        const batchItems = [];
        for (const raw of articles) {
            const item = pubmedArticleToItem(raw);
            if (item)
                batchItems.push(item);
        }
        const byPmid = new Map();
        for (const it of batchItems) {
            const id = pmidFromItemHref(it.href || it.link);
            if (id)
                byPmid.set(id, it);
        }
        for (const id of chunk) {
            const hit = byPmid.get(id);
            if (hit)
                ordered.push(hit);
        }
    }
    return ordered;
}
function applyAbstractMaxChars(items, abstractMax) {
    if (abstractMax == null || abstractMax <= 0)
        return;
    for (const it of items) {
        const b = it.body ?? "";
        if (b.length > abstractMax) {
            const t = `${b.slice(0, abstractMax).trimEnd()}…`;
            it.body = t;
            it.description = t;
        }
    }
}
/**
 * Incremental PubMed load: ESearch PMID list (cached), then EFetch only the requested window.
 */
async function fetchPubMedArticlesSlice(options) {
    const term = options.term.trim();
    if (!term) {
        return { items: [], totalItems: 0, hasMore: false };
    }
    const requireAbstract = options.requireAbstract !== false;
    const esearchTerm = requireAbstract ? `(${term}) AND hasabstract` : term;
    const offset = Math.max(0, Math.floor(options.offset));
    const limit = Math.max(0, Math.floor(options.limit));
    const sliceKey = `${pmidListCacheKey(esearchTerm)}::${offset}::${limit}::${requireAbstract ? 1 : 0}`;
    return getPubmedSliceCache().getOrCreate(sliceKey, async () => {
        const hasKey = Boolean(options.apiKey?.trim());
        const throttle = createThrottle(hasKey);
        const maxList = Math.min(Math.max(1, Math.floor(options.maxPmidListSize)), PUBMED_ESEARCH_MAX);
        const pmids = await loadPmidList(esearchTerm, maxList, options, throttle);
        const totalListed = pmids.length;
        const slice = pmids.slice(offset, offset + limit);
        if (slice.length === 0) {
            return {
                items: [],
                totalItems: totalListed,
                hasMore: offset < totalListed,
            };
        }
        const items = await efetchPubmedByPmids(slice, options, throttle);
        applyAbstractMaxChars(items, options.abstractMaxChars);
        const hasMore = offset + slice.length < totalListed;
        return { items, totalItems: totalListed, hasMore };
    });
}
/**
 * One-shot fetch (same as slice with offset 0, limit = maxRecords).
 */
async function fetchPubMedArticlesAsRssItems(options) {
    const maxWant = Math.max(0, Math.min(Math.floor(options.maxRecords) || 0, PUBMED_ESEARCH_MAX));
    if (maxWant <= 0)
        return [];
    const listCap = Math.min(maxWant, (0, rssRouteLimits_1.pubmedIncrementalMaxPmidList)());
    const r = await fetchPubMedArticlesSlice({
        ...options,
        offset: 0,
        limit: maxWant,
        maxPmidListSize: listCap,
    });
    const sorted = [...r.items].sort((a, b) => pubDateToUtcMs(b.pubDate) - pubDateToUtcMs(a.pubDate));
    return sorted;
}
